import { initializeApp } from "firebase/app";
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  sendEmailVerification,
  PhoneAuthProvider,
  RecaptchaVerifier,
  getMultiFactorResolver,
  PhoneMultiFactorGenerator,
  sendPasswordResetEmail,
} from "firebase/auth";

import { getFunctions } from "firebase/functions";

const config = {
  projectId: "enliv-342806",
  apiKey: "AIzaSyCWUzeZ7-Xf8fZCWJULHYDbc7poG4F3_ik",
  authDomain: "enliv-342806.firebaseapp.com",
};

const firebaseApp = initializeApp(config);
const auth = getAuth(firebaseApp);

// Auth persistence — controls how Firebase stores the signed-in user
// across browser navigation. Default ("local") writes to IndexedDB,
// which is shared across all tabs of the same origin; signing in as
// user B in one tab silently changes auth.currentUser to B in every
// other tab of the same browser. That's the right UX for production
// (users expect one login per browser, not per tab), but it makes
// concurrent multi-account testing impossible — you can't have
// "Doctor A in Tab 1, Doctor B in Tab 2" because Firebase merges them
// into whichever logged in last.
//
// Setting `REACT_APP_AUTH_PERSISTENCE=session` switches to
// browserSessionPersistence: auth is scoped to the tab's
// sessionStorage, so each tab keeps its own user. Use this in
// `.env.dev` when you need to exercise the pair-token cross-routing
// defences with two real Firebase users in the same browser.
//
// setPersistence returns a Promise; we fire-and-forget at module load
// because no user interaction can happen before this resolves (the
// app's React tree hasn't mounted yet, let alone a login form). If a
// fire-and-forget feels uncomfortable, swap the export to an
// async-ready helper — but the Firebase docs use exactly this
// pattern.
const _envPersistence = (
  process.env.REACT_APP_AUTH_PERSISTENCE || "local"
).toLowerCase();

// Production safety net.
//
// "session" persistence is strictly a dev convenience for exercising
// the copilot's pair-token cross-routing defences with two real
// Firebase users in one browser. In a production build it would
// force every user to re-login on every new tab, which is a real UX
// regression. The accidental path here is comment-block copy-paste
// from .env.dev into .env.prod — easy to do, hard to notice in
// review, very obvious to users (so it WILL get caught, just after
// it's already shipped).
//
// CRA sets NODE_ENV to "production" only inside `react-scripts build`.
// If we detect that AND see session-mode in env, we rewrite to
// "local" so the bundle stays correct, and emit a loud console.error
// that any QA / production smoke test will see in DevTools the
// moment the page loads. Failing fast (throwing) would also work,
// but rewriting keeps the app functional while still being noisy
// enough to catch.
const _isProductionBuild = process.env.NODE_ENV === "production";
let _authPersistenceMode = _envPersistence;
if (_isProductionBuild && _envPersistence === "session") {
  // eslint-disable-next-line no-console
  console.error(
    "[firebase] REACT_APP_AUTH_PERSISTENCE=session is not allowed in "
      + "production builds — it would force users to re-login on every "
      + "new tab. Forcing to 'local' for safety. Fix .env.prod and rebuild.",
  );
  _authPersistenceMode = "local";
}

const _resolvedPersistence =
  _authPersistenceMode === "session"
    ? browserSessionPersistence
    : browserLocalPersistence;
setPersistence(auth, _resolvedPersistence).catch((err) => {
  // eslint-disable-next-line no-console
  console.warn("[firebase] setPersistence failed", {
    mode: _authPersistenceMode,
    error: err?.message ?? String(err),
  });
});

const functionsInstance = getFunctions(firebaseApp, "us-east4");

export {
  auth,
  functionsInstance,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  sendEmailVerification,
  PhoneAuthProvider,
  RecaptchaVerifier,
  getMultiFactorResolver,
  PhoneMultiFactorGenerator,
  sendPasswordResetEmail,
};
